(() => {
  const rootSelector = '[data-selection-scroll-morph]';
  const pairNames = ['coffee', 'matcha'];

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const waitForMedia = (container) => {
    const media = Array.from(container.querySelectorAll('img, video'));

    return Promise.all(
      media.map((mediaElement) => {
        if (mediaElement.tagName === 'IMG') {
          mediaElement.loading = 'eager';
          if (mediaElement.complete) return Promise.resolve();
        } else if (mediaElement.readyState >= 2) {
          return Promise.resolve();
        }

        return new Promise((resolve) => {
          mediaElement.addEventListener(mediaElement.tagName === 'VIDEO' ? 'loadeddata' : 'load', resolve, { once: true });
          mediaElement.addEventListener('error', resolve, { once: true });
        });
      })
    );
  };

  const stripCloneAttributes = (clone) => {
    clone.removeAttribute('id');
    clone.removeAttribute('href');
    clone.removeAttribute('tabindex');
    clone.removeAttribute('data-scroll-morph-source');
    clone.removeAttribute('data-scroll-morph-target');
    clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    clone.querySelectorAll('a').forEach((node) => {
      node.removeAttribute('href');
      node.removeAttribute('tabindex');
    });
  };

  const createPair = (root, name) => {
    const source = root.querySelector(`[data-scroll-morph-source="${name}"]`);
    const target = root.querySelector(`[data-scroll-morph-target="${name}"]`);
    if (!source || !target) return null;

    const proxy = document.createElement('div');
    proxy.className = `the-moon-home__scroll-morph-proxy the-moon-home__scroll-morph-proxy--${name}`;
    proxy.setAttribute('aria-hidden', 'true');

    const sourceVisual = document.createElement('div');
    sourceVisual.className = 'the-moon-home__scroll-morph-visual the-moon-home__scroll-morph-visual--source';
    const sourceClone = source.cloneNode(true);
    stripCloneAttributes(sourceClone);
    sourceClone.querySelectorAll('.the-moon-home__hero-landscape-photo').forEach((photo) => {
      photo.style.opacity = '1';
      photo.style.visibility = 'visible';
    });
    sourceClone.querySelectorAll('.the-moon-home__hero-landscape-photo img, .the-moon-home__hero-landscape-photo video').forEach((mediaElement) => {
      mediaElement.style.transform = 'none';
    });
    sourceVisual.appendChild(sourceClone);

    const targetVisual = document.createElement('div');
    targetVisual.className = 'the-moon-home__scroll-morph-visual the-moon-home__scroll-morph-visual--target';
    targetVisual.style.setProperty('--selection-card-mask', target.style.getPropertyValue('--selection-card-mask'));

    const targetArt = target.querySelector('.the-moon-home__selection-card-art');
    if (targetArt) {
      const artClone = targetArt.cloneNode(true);
      stripCloneAttributes(artClone);
      targetVisual.appendChild(artClone);
    }

    target.querySelectorAll('.the-moon-home__selection-default-label').forEach((label) => {
      const labelClone = label.cloneNode(true);
      stripCloneAttributes(labelClone);
      targetVisual.appendChild(labelClone);
    });

    proxy.append(sourceVisual, targetVisual);
    root.appendChild(proxy);

    return {
      name,
      proxy,
      source,
      sourceVisual,
      target,
      targetLabels: Array.from(targetVisual.querySelectorAll('.the-moon-home__selection-default-label')),
      targetVisual
    };
  };

  const initialiseRoot = (root) => {
    if (root.dataset.selectionScrollMorphReady === 'true' || !window.gsap) return;

    const hero = root.querySelector('[data-scroll-morph-stage]');
    const selection = root.querySelector('[data-scroll-morph-end]');
    if (!hero || !selection) return;

    const pairs = pairNames.map((name) => createPair(root, name)).filter(Boolean);
    if (pairs.length !== pairNames.length) {
      pairs.forEach((pair) => pair.proxy.remove());
      return;
    }

    root.dataset.selectionScrollMorphReady = 'true';

    const media = window.gsap.matchMedia();
    root.selectionScrollMorphMedia = media;

    media.add(
      {
        desktop: '(min-width: 750px)',
        motionSafe: '(prefers-reduced-motion: no-preference)'
      },
      (context) => {
        if (!context.conditions.desktop || !context.conditions.motionSafe) return;

        let animation = null;
        let endScroll = 1;
        let frameRequested = false;
        let isActive = false;
        let refreshRequested = false;
        let resizeObserver = null;
        let startScroll = 0;

        const setActive = (active) => {
          if (active === isActive) return;
          isActive = active;

          pairs.forEach((pair) => {
            pair.source.style.visibility = active ? 'hidden' : '';
            pair.target.style.visibility = active ? 'hidden' : '';
            pair.proxy.style.opacity = active ? '1' : '0';
            pair.proxy.style.visibility = active ? 'visible' : 'hidden';
          });
        };

        const update = () => {
          frameRequested = false;
          if (!animation) return;

          const progress = clamp((window.scrollY - startScroll) / (endScroll - startScroll), 0, 1);
          animation.progress(progress, false);
          setActive(progress > 0.001 && progress < 0.999);
        };

        const requestUpdate = () => {
          if (frameRequested) return;
          frameRequested = true;
          window.requestAnimationFrame(update);
        };

        const refresh = () => {
          if (!root.isConnected) return;

          const currentScroll = window.scrollY;
          const heroRect = hero.getBoundingClientRect();
          const selectionRect = selection.getBoundingClientRect();
          const heroTop = heroRect.top + currentScroll;
          const selectionTop = selectionRect.top + currentScroll;

          startScroll = heroTop + Math.min(64, heroRect.height * 0.06);
          endScroll = Math.max(startScroll + 1, selectionTop);

          if (animation) animation.kill();
          animation = window.gsap.timeline({ paused: true });

          pairs.forEach((pair) => {
            const sourceRect = pair.source.getBoundingClientRect();
            const targetRect = pair.target.getBoundingClientRect();
            const sourceDocumentTop = sourceRect.top + currentScroll;
            const targetDocumentTop = targetRect.top + currentScroll;
            const endScaleX = targetRect.width / Math.max(sourceRect.width, 1);
            const endScaleY = targetRect.height / Math.max(sourceRect.height, 1);

            window.gsap.set(pair.proxy, {
              height: sourceRect.height,
              scaleX: 1,
              scaleY: 1,
              width: sourceRect.width,
              x: sourceRect.left,
              y: sourceDocumentTop - startScroll
            });
            window.gsap.set(pair.sourceVisual, { autoAlpha: 1 });
            window.gsap.set(pair.targetVisual, { autoAlpha: 0 });
            if (pair.targetLabels.length) {
              window.gsap.set(pair.targetLabels, {
                autoAlpha: 0,
                scaleX: 1,
                scaleY: 1,
                transformOrigin: '0 0'
              });
            }

            animation.to(
              pair.proxy,
              {
                duration: 1,
                ease: 'none',
                scaleX: endScaleX,
                scaleY: endScaleY,
                x: targetRect.left,
                y: targetDocumentTop - endScroll
              },
              0
            );
            animation.to(
              pair.sourceVisual,
              { autoAlpha: 0, duration: 0.46, ease: 'power1.inOut' },
              0.27
            );
            animation.to(
              pair.targetVisual,
              { autoAlpha: 1, duration: 0.46, ease: 'power1.inOut' },
              0.27
            );

            if (pair.targetLabels.length) {
              animation.to(
                pair.targetLabels,
                {
                  duration: 0.73,
                  ease: 'none',
                  scaleX: 1 / Math.max(endScaleX, 0.001),
                  scaleY: 1 / Math.max(endScaleY, 0.001)
                },
                0.27
              );
              animation.to(
                pair.targetLabels,
                { autoAlpha: 1, duration: 0.2, ease: 'power1.out' },
                0.72
              );
            }
          });

          update();
        };

        const requestRefresh = () => {
          if (refreshRequested) return;
          refreshRequested = true;
          window.requestAnimationFrame(() => {
            refreshRequested = false;
            refresh();
          });
        };

        window.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', requestRefresh, { passive: true });
        window.addEventListener('orientationchange', requestRefresh, { passive: true });

        if ('ResizeObserver' in window) {
          resizeObserver = new ResizeObserver(requestRefresh);
          resizeObserver.observe(hero);
          resizeObserver.observe(selection);
        }

        Promise.all(pairs.map((pair) => waitForMedia(pair.proxy))).then(refresh);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(requestRefresh);

        return () => {
          window.removeEventListener('scroll', requestUpdate);
          window.removeEventListener('resize', requestRefresh);
          window.removeEventListener('orientationchange', requestRefresh);
          if (resizeObserver) resizeObserver.disconnect();
          if (animation) animation.kill();
          setActive(false);
        };
      }
    );
  };

  const initialiseAll = (scope = document, attempt = 0) => {
    if (!window.gsap) {
      if (attempt < 300) window.requestAnimationFrame(() => initialiseAll(scope, attempt + 1));
      return;
    }

    if (scope.matches && scope.matches(rootSelector)) initialiseRoot(scope);
    scope.querySelectorAll(rootSelector).forEach(initialiseRoot);
  };

  const destroyAll = (scope) => {
    const roots = [];
    if (scope.matches && scope.matches(rootSelector)) roots.push(scope);
    roots.push(...scope.querySelectorAll(rootSelector));

    roots.forEach((root) => {
      if (root.selectionScrollMorphMedia) root.selectionScrollMorphMedia.revert();
      root.querySelectorAll('.the-moon-home__scroll-morph-proxy').forEach((proxy) => proxy.remove());
      delete root.selectionScrollMorphMedia;
      delete root.dataset.selectionScrollMorphReady;
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
